import React from 'react';
import { Target, ArrowRight, SkipForward } from 'lucide-react';
import { CISA_FUNCTIONS, CISA_PILLARS, MATURITY_CRITERIA } from '../../../config/cisaMaturityModel';
import { getDiagramTemplate } from '../data/maturityDiagramTemplates';

/**
 * Maturity levels in order
 */
const MATURITY_LEVELS = [
  { key: 'TRADITIONAL', label: 'Traditional', color: 'var(--zt-traditional, #6b7280)' },
  { key: 'INITIAL', label: 'Initial', color: 'var(--zt-initial, #eab308)' },
  { key: 'ADVANCED', label: 'Advanced', color: 'var(--zt-advanced, #3b82f6)' },
  { key: 'OPTIMAL', label: 'Optimal', color: 'var(--zt-optimal, #10b981)' }
];

/**
 * JourneyAssessment Component
 *
 * Two-phase assessment flow:
 * 1. "Where am I today?" - Select current maturity level
 * 2. "Where do I want to be?" - Select target maturity level
 */
function JourneyAssessment({
  functionCode,
  pillarCode,
  currentLevel,
  targetLevel,
  assessmentPhase,
  onSelectLevel,
  onBack,
  onSkipToGap
}) {
  // Get function and pillar info
  const pillar = CISA_PILLARS.find(p => p.code === pillarCode);
  const fn = CISA_FUNCTIONS[pillarCode]?.find(f => f.code === functionCode);

  const isCurrentPhase = assessmentPhase === 'CURRENT';
  const currentIndex = MATURITY_LEVELS.findIndex(l => l.key === currentLevel);

  // For target selection, only show levels above current
  const availableLevels = isCurrentPhase
    ? MATURITY_LEVELS
    : MATURITY_LEVELS.filter((_, idx) => idx > currentIndex);

  return (
    <div className="ztj-assessment">
      {/* Header */}
      <div className="ztj-assessment-header">
        <h2>
          {isCurrentPhase ? (
            <>
              <Target size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Where are you today?
            </>
          ) : (
            <>
              <ArrowRight size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Where do you want to be?
            </>
          )}
        </h2>
        <p>
          {isCurrentPhase
            ? `Select your current maturity level for ${fn?.name || functionCode}`
            : `Select your target maturity level for ${fn?.name || functionCode}`}
        </p>
        {!isCurrentPhase && (
          <button
            type="button"
            className="ztj-action-btn"
            onClick={onSkipToGap}
            style={{ marginTop: 'var(--space-3)' }}
          >
            <SkipForward size={16} />
            Skip to Gap Analysis
          </button>
        )}
      </div>

      {/* Level selection cards */}
      <div className="ztj-assessment-levels">
        {availableLevels.map(level => {
          const template = getDiagramTemplate(functionCode, level.key);
          const criteria = MATURITY_CRITERIA[functionCode]?.[level.key];
          const isSelected = isCurrentPhase
            ? level.key === currentLevel
            : level.key === targetLevel;
          const isDisabled = !isCurrentPhase && level.key === currentLevel;

          return (
            <button
              key={level.key}
              type="button"
              className={`ztj-assessment-level-card ztj-level--${level.key.toLowerCase()} ${isSelected ? 'ztj-assessment-level-card--selected' : ''} ${isDisabled ? 'ztj-assessment-level-card--disabled' : ''}`}
              onClick={() => !isDisabled && onSelectLevel(level.key)}
              disabled={isDisabled}
              style={{
                '--level-color': level.color,
                '--level-color-light': `${level.color}10`
              }}
            >
              <div className="ztj-assessment-level-header">
                <div
                  className="ztj-assessment-level-dot"
                  style={{ background: level.color }}
                />
                <span className="ztj-assessment-level-name">{level.label}</span>
              </div>

              <div className="ztj-assessment-level-description">
                {template?.name || level.label}
              </div>

              {template?.keyCapabilities && (
                <ul className="ztj-assessment-level-capabilities">
                  {template.keyCapabilities.slice(0, 3).map((cap, idx) => (
                    <li key={idx}>{cap}</li>
                  ))}
                </ul>
              )}

              {isSelected && (
                <div style={{
                  marginTop: 'auto',
                  paddingTop: 'var(--space-2)',
                  fontSize: 'var(--text-xs)',
                  color: level.color,
                  fontWeight: 600
                }}>
                  {isCurrentPhase ? 'Current Selection' : 'Target Selection'}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Current selection info */}
      {isCurrentPhase && currentLevel && (
        <div className="ztj-side-panel">
          <h3>
            Selected: {MATURITY_LEVELS.find(l => l.key === currentLevel)?.label}
          </h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', margin: 0 }}>
            {MATURITY_CRITERIA[functionCode]?.[currentLevel] || 'No criteria defined'}
          </p>
        </div>
      )}

      {/* Phase indicator */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)'
      }}>
        <div style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: isCurrentPhase ? 'var(--color-primary)' : 'var(--color-border)'
        }} />
        <div style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: !isCurrentPhase ? 'var(--color-primary)' : 'var(--color-border)'
        }} />
      </div>
    </div>
  );
}

export default JourneyAssessment;
